import Backbone from 'backbone'
import _ from 'lodash'
import $ from 'jquery'
import appDisplay from 'scoreboard/components/app-display'
import i18n from 'scoreboard/components/i18n'
import Tooltip from 'scoreboard/components/tooltip'
import ReportColumnChooser from 'scoreboard/reports/common/report-column-chooser'
import ChartDesignerPanel from './chart-designer-panel'
import inactiveTemplate from './x-axis-panel-inactive-content.hbs'
import activeTemplate from './x-axis-panel-active-content.hbs'
import BreakdownView from './breakdown-view'
import axesText from './axes-text'
import EditBreakdownView from './edit-breakdown-view'
import validBreakdownFieldIds from './valid-breakdown-field-ids'
import ChartDesignerBreakdown from '../model/chart-designer-breakdown'
import breakdownRangeLabels from '../breakdown-range-labels'
import nonAxesPlotType from 'scoreboard/charts/non-axes-plot-type'
import TooltipAndDetails from 'scoreboard/components/tooltip-and-details'

/**
 * @extends ChartDesignerPanel
 */
const XAxisPanel = ChartDesignerPanel.extend(/** @lends XAxisPanel.prototype */{
	className: ChartDesignerPanel.prototype.className + ' x-axis-panel',

	/**
	 * @type ChartDesigner
	 */
	model: null,

	/** @type {ChartData} */
	data: null,

	events: {
		'click .add-breakdown-button': 'onAddBreakdownClick'
	},

	initialize: function (options = {}) {
		ChartDesignerPanel.prototype.initialize.apply(this, arguments)
		this.data = this.model.data || options.data
		this.title = i18n.get('charts.breakdown.repeatNoArgs')
	},

	/**
	 * @override
	 */
	renderActiveContent: function (container) {
		let childrenTotal =container.children().length
		if (childrenTotal === 0) {
			container.append(activeTemplate())
		}
		// Replace any breakdown summaries badges with updated badges
		this.$('.breakdown-summaries').remove()
		// #42063 ensure these are always rendered in the correct order
		const sortedBreakdowns = _.sortBy(this.model.breakdowns.toArray(),
			[b => b.get('sortOrder')])
		container.prepend(inactiveTemplate({
			breakdowns: sortedBreakdowns.map(breakdown => {
				const rangeSublabels = breakdown.ranges.map(range =>
					breakdownRangeLabels({
						data: this.data, breakdown, range
					}).sublabel
				)
				return {
					label: this.model.breakdownLabel(breakdown),
					ranges: rangeSublabels.map(sublabel => ({ sublabel })),
					error: !!breakdown.get('error')
				}
			})
		}))

		this.renderFirstBreakdown()
		this.renderSecondBreakdown()
		this.renderAddButton()

		let addButtons = this.$('.add-buttons')
		let hasButtons = addButtons.find('.button:not(.is-not-displayed)').length
		addButtons.toggleClass('is-not-displayed', !hasButtons)
		this.$('.chart-designer-panel-content')
			.toggleClass('is-empty', !this.model.breakdowns.length)
	},

	toggleActive: function (active) {
		ChartDesignerPanel.prototype.toggleActive.apply(this, arguments)

		if (!active) {
			this.removeAddBreakdownTooltip()
			_.invokeMap([this.firstBreakdownView, this.secondBreakdownView],
				 'inactive')
		}
	},

	renderFirstBreakdown: function () {
		if (this.model.breakdowns.length === 0) {
			return
		}

		if (this.firstBreakdownView) {
			return
		}

		this.firstBreakdownView = this.renderBreakdown(0)
	},

	renderSecondBreakdown: function () {
		if (this.model.breakdowns.length < 2) {
			return
		}

		if (this.secondBreakdownView) {
			return
		}

		this.secondBreakdownView = this.renderBreakdown(1)
	},

	renderBreakdown: function (sortOrder) {
		let breakdown = this.model.breakdowns.find(b => b.get('sortOrder') == sortOrder)

		let view = new BreakdownView({
			model: breakdown,
			chart: this.model,
			data: this.data
		})

		this.$('.breakdowns').removeClass('badges').append(view.el)

		this.listenTo(breakdown, 'remove', () => {
			this.removeBreakdownView(view)
			this.renderActiveContent(this.activeContent())
		})

		this.listenTo(view, 'swap', () => this.onBreakdownSwap())

		this.listenTo(view, 'change:sortSettings', () => this.trigger('change:sortSettings'))

		view.render()

		return view
	},

	onBreakdownSwap: function () {
		let b1 = this.model.breakdowns.find(b => b.get('sortOrder') === 0)
		let b2 = this.model.breakdowns.find(b => b.get('sortOrder') === 1)

		b1.set('sortOrder', 1)
		b2.set('sortOrder', 0)

		let first = this.firstBreakdownView
		let second = this.secondBreakdownView

		first.$el.appendTo(this.$('.breakdowns'))

		this.firstBreakdownView = second
		this.secondBreakdownView = first
	},

	renderAddButton: function () {
		const isDatasetSourceOnly = this.model.hasDatasetSources()
			&& !this.model.hasScorecardItemSources()
			&& !this.model.hasInitiativeSources()

		let buttonText
		let disabled = false
		if (this.model.breakdowns.length === 0) {
			let code = (this.model.hasInitiativeSources() && !this.model.hasDatasetSources())
				? 'charts.breakdown.repeatAxisByCalendarPeriod'
				: 'charts.breakdown.repeatAxisBy'

			if (nonAxesPlotType(this.model)) {
				code += '.noAxes'
			}
			buttonText = i18n.get(code, axesText(this.model))
		} else if (this.model.breakdowns.length === 1) {
			if (isDatasetSourceOnly && !nonAxesPlotType(this.model)) {

				// Can only breakdown once if there is scorecard or initiative data
				buttonText = i18n.get('charts.breakdown.repeatAgainBy')
			}
		}

		this.removeDatasetsNotReadyTooltip()
		if (!this._allDatasetsAvailable()) {
			// #45194 don't allow editing breakdowns until the chart has finished
			// loading all needed data
			disabled = true
			this.datasetsNotReadyTooltip = new TooltipAndDetails({
				el: this.el,
				selector: '.add-breakdown-button',
				tooltipContent: () => i18n.get('charts.breakdown.error.datasetsNotReady'),
				tooltipOptions: {
					extraClass: 'tooltip-body-only'
				}
			})
		}

		disabled = disabled || !this.model.features.length

		this.$('.add-breakdown-button')
			.toggleClass('is-not-displayed', !buttonText)
			.toggleClass('is-disabled', disabled)
			.find('.button-text').text(buttonText)
	},

	/**
	 * #45194 ensures that all datasets referenced in all serie sources have been
	 * loaded from the server
	 */
	_allDatasetsAvailable: function() {
		const series = this.model.getSerieFeatures()
			.filter(s => s.get('source').domainType === 'dataset')
		for (const serie of series) {
			const aggregation = serie.get('source').aggregation
			// #45432 apparently a data source can be defined as `calculated` either
			// by its `dataSourceType` of `calculated` field...
			if (aggregation.dataSourceType === 'basic' || aggregation.calculated === false) {
				if (!this.model.data.datasetModel(aggregation.startingDatasetId)) return false
			} else {
				for (const agg of aggregation.aggregations) {
					if (!this.model.data.datasetModel(agg.datasetId)) return false
				}
			}
		}

		return true
	},

	removeDatasetsNotReadyTooltip: function() {
		this.datasetsNotReadyTooltip && this.datasetsNotReadyTooltip.remove()
	},

	removeBreakdownView: function (view) {
		view.remove()
		if (this.firstBreakdownView === view) {
			this.firstBreakdownView = this.secondBreakdownView
			delete this.secondBreakdownView
		} else if (this.secondBreakdownView === view) {
			delete this.secondBreakdownView
		}
	},

	onAddBreakdownClick: function () {
		if (this.$('.add-breakdown-button').hasClass('is-disabled')) return

		if (this.model.hasDatasetSources()) {
			this.renderAddBreakdownTooltip()
		} else {
			this.model.breakdowns.add(ChartDesignerBreakdown.calendarPeriodBreakdown(this.model))
		}
	},

	renderAddBreakdownTooltip: function () {
		this.removeAddBreakdownTooltip()

		let dataset = this.model.getDatasetDatasource()

		if (!dataset || this.model.hasMultipleDatasetSources()) {
			// We don't have a starting dataset or we have multiple referenced
			// datasets; just open the "More Options" dialog directly
			this.openMoreOptionsDialog()
			return
		}

		let onAdd = fieldIdentifier => {
			let breakdownAttrs = ChartDesignerBreakdown.datasetFieldBreakdown({
				chartDesigner: this.model,
				dataset,
				fieldIdentifier
			})

			return this.validateAndAddBreakdown(breakdownAttrs)
		}

		let validFieldIds = validBreakdownFieldIds({
			chart: this.model,
			dataset,
			dateFieldsOnly: this.model.hasMultipleDatasetSources()
		})

		// We only want direct fields
		const allowedFieldIds = _.map(dataset.get('fields'), f => f.id).filter(id =>
			!validFieldIds.fieldIds || validFieldIds.fieldIds.includes(id))

		let newBreakdownSelector = new ReportColumnChooser({
			dataset,
			headerButtonLabel: 'report.designer.custom',
			onAdd,
			datasetDropdownOptions: {
				allowedFieldIds,
				allowedVisibleLinkedFieldIds: validFieldIds.visibleLinkedFieldIds
			}
		})

		this.listenTo(newBreakdownSelector, 'details',
			fieldIdentifier => this.openMoreOptionsDialog({fieldIdentifier}))

		this.addBreakdownTooltip = new Tooltip({
			attachTo: this.$('.add-breakdown-button'),
			content: newBreakdownSelector,
			placement: 'above',
			arrow: 'center',
			extraClass: 'designer-dataset-field-chooser chart-designer-tooltip'
		}).render()
	},

	openMoreOptionsDialog: function ({ fieldIdentifier, breakdownAttrs } = {}) {
		let dataset = this.model.getDatasetDatasource()

		if (!breakdownAttrs) {
			breakdownAttrs = fieldIdentifier
				? ChartDesignerBreakdown.datasetFieldBreakdown({
					chartDesigner: this.model,
					dataset,
					fieldIdentifier
				})
				: ChartDesignerBreakdown.basicDatasetBreakdown({
					chartDesigner: this.model,
					dataset
				})
		}

		let breakdown = new ChartDesignerBreakdown(breakdownAttrs)

		let editView = new EditBreakdownView({
			model: breakdown,
			chart: this.model,
			dataset,
			isNewBreakdown: true
		})

		appDisplay.dialog(editView)

		this.listenToOnce(editView, 'done', (updatedDesigner) => {
			// Note: EditBreakdownView already validated the breakdown changes,
			// so we can simply apply the changes to the model
			this.model.set(updatedDesigner)
		})
	},

	validateAndAddBreakdown: function (breakdownAttrs) {
		// #46066 after the field is selected, remove the tooltip and go to
		// loading state while the server validates/tweaks the breakdown
		this.removeAddBreakdownTooltip()
		this.trigger('loading')

		breakdownAttrs.sortOrder = this.model.breakdowns.length

		let request = {
			chartDesigner: this.model.toPostJSON(),
			breakdown: breakdownAttrs,
			rangeThreshold: EditBreakdownView.RANGE_WARNING_THRESHOLD
		}

		return $.ajax({
			method: 'POST',
			url: `api/charts/validate-breakdown`,
			data: JSON.stringify(request)
		}).then(response => {
			// The response will contain an entire chart designer that may be
			// tweaked from what was originally sent (like automatically generated
			// breakdown ranges)
			this.model.set(response)
		})
	},

	removeAddBreakdownTooltip: function () {
		this.addBreakdownTooltip && this.addBreakdownTooltip.remove()
		delete this.addBreakdownTooltip
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([ this.firstBreakdownView, this.secondBreakdownView ], 'remove')
		this.removeAddBreakdownTooltip()
		this.removeDatasetsNotReadyTooltip()
	}
})

export default XAxisPanel

XAxisPanel.__filename__ = 'charts/edit/x-axis-panel.js'
